import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Button, IconButton, InputAdornment, Link, Paper, Switch, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

import { AppOutlinedTextField } from '@/components/shared/AppOutlinedTextField';
import { changePassword, getCurrentUser, getNutritionistProfile, updateStoredUserProfile } from '@/lib/apiService';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';

interface PasswordFormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

type PasswordVisibilityState = Record<keyof PasswordFormState, boolean>;

interface ActiveSession {
  id: string;
  deviceLabel: string;
  locationLabel: string;
  isCurrent: boolean;
}

const securitySwitchSx: SxProps<Theme> = {
  width: 42,
  height: 24,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0,
    m: '2px',
    color: '#fff',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        bgcolor: '#3DC6BA',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.22)',
    border: 'none',
    bgcolor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: '999px',
    bgcolor: '#DDE3E8',
    opacity: 1,
  },
};

const DEFAULT_PASSWORD_FORM: PasswordFormState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const DEFAULT_PASSWORD_VISIBILITY: PasswordVisibilityState = {
  currentPassword: false,
  newPassword: false,
  confirmPassword: false,
};

const settingsCardSx: SxProps<Theme> = {
  borderRadius: '14px',
  border: '1px solid transparent',
  borderBottomColor: '#e4ebf1',
  bgcolor: '#fff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
  px: { xs: 2, sm: 3 },
  py: { xs: 2.35, sm: 2.8 },
};

const roomyFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    borderRadius: '12px',
    alignItems: 'center',
    '& input': {
      paddingTop: '14px',
      paddingBottom: '14px',
      paddingLeft: '16px',
      paddingRight: '16px',
      fontSize: '0.94rem',
    },
  },
  '& .MuiInputAdornment-positionEnd': {
    alignItems: 'center',
    alignSelf: 'center',
    height: 'auto',
    maxHeight: 'none',
    marginTop: 0,
  },
};

const SecuritySettingsView: React.FC = () => {
  const navigate = useNavigate();
  const [authProvider, setAuthProvider] = useState(getCurrentUser()?.authProvider);
  const hasPasswordAuth = authProvider === 'password' || authProvider === 'password+google';
  const hasGoogleAuth = authProvider === 'google' || authProvider === 'password+google';
  const isGoogleOnlyUser = hasGoogleAuth && !hasPasswordAuth;

  useEffect(() => {
    let cancelled = false;
    // Refresh pre-existing browser sessions from the server's stored provider.
    void getNutritionistProfile().then(({ data }) => {
      if (cancelled) return;
      setAuthProvider(data.authProvider);
      updateStoredUserProfile({ authProvider: data.authProvider });
    }).catch(error => {
      if (!cancelled) showErrorToast(error, 'Could not load account security settings');
    });
    return () => { cancelled = true; };
  }, []);
  const [passwordForm, setPasswordForm] = useState<PasswordFormState>(DEFAULT_PASSWORD_FORM);
  const [passwordVisibility, setPasswordVisibility] = useState<PasswordVisibilityState>(DEFAULT_PASSWORD_VISIBILITY);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const updatePasswordField = <K extends keyof PasswordFormState,>(field: K, value: PasswordFormState[K]) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field: keyof PasswordFormState) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const renderPasswordVisibilityAdornment = (field: keyof PasswordFormState, label: string) => {
    const isVisible = passwordVisibility[field];

    return (
      <InputAdornment position="end" sx={{ alignItems: 'center', alignSelf: 'center', height: 'auto', mt: 0 }}>
        <IconButton
          aria-label={isVisible ? `Hide ${label}` : `Show ${label}`}
          edge="end"
          disabled={isUpdatingPassword}
          onClick={() => togglePasswordVisibility(field)}
          onMouseDown={(event) => event.preventDefault()}
          sx={{ p: 0.75, color: '#7B8A96' }}
        >
          {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
        </IconButton>
      </InputAdornment>
    );
  };

  const handleUpdatePassword = async () => {
    if (isUpdatingPassword) return;

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      showWarningToast('Missing fields', 'Please fill all password fields to continue.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showWarningToast('Passwords do not match', 'New password and confirm password should be identical.');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      showWarningToast('Password too short', 'Use at least 8 characters for stronger security.');
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      showWarningToast('Choose a new password', 'Your new password should be different from your current one.');
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmNewPassword: passwordForm.confirmPassword,
      });
      setPasswordForm(DEFAULT_PASSWORD_FORM);
      setPasswordVisibility(DEFAULT_PASSWORD_VISIBILITY);
      showSuccessToast('Password updated', 'Your password has been changed successfully.');
    } catch (error) {
      showErrorToast(error, 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f7f9fb',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: '1px solid #dbe6eb',
              bgcolor: '#f8fafc',
              px: { xs: 2, sm: 3 },
              py: 1.25,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link component={RouterLink} to="/profile?view=hub" underline="hover" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#64748b' }}>
                Settings
              </Link>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Security Settings
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3.5, lg: 4 }, pb: { xs: 3, sm: 4 }, pt: { xs: 2.3, sm: 2.8 } }}>
          <Box sx={{ mb: { xs: 2.8, sm: 3.2 }, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <IconButton
              aria-label="Back to settings"
              onClick={() => navigate('/profile?view=hub')}
              sx={{
                mt: 0.3,
                width: 30,
                height: 30,
                color: '#1F2B34',
                p: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                },
              }}
            >
              <ArrowLeft size={28} strokeWidth={2.1} />
            </IconButton>

            <Box sx={{ minWidth: 0, pt: 0.05 }}>
              <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: { xs: '1.6rem', sm: '1.92rem' }, lineHeight: 1.05 }}>
                Security Settings
              </Typography>
              <Typography sx={{ mt: 0.65, color: '#676B80', fontSize: { xs: '0.8rem', sm: '0.92rem' }, lineHeight: 1.2 }}>
                Manage your password and enable two-factor authentication.
              </Typography>
            </Box>
          </Box>

          <Box
            component={hasPasswordAuth ? 'form' : 'section'}
            onSubmit={(event) => {
              event.preventDefault();
              if (hasPasswordAuth) {
                void handleUpdatePassword();
              }
            }}
            sx={settingsCardSx}
          >
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: '#1B232A', fontWeight: 700, fontSize: '1.02rem' }}>Change Password</Typography>
                  {isGoogleOnlyUser ? (
                    <Typography sx={{ mt: 0.4, color: '#7B8B97', fontSize: '0.84rem' }}>
                      You signed in with Google.
                    </Typography>
                  ) : hasGoogleAuth ? (
                    <Typography sx={{ mt: 0.4, color: '#7B8B97', fontSize: '0.84rem' }}>
                      Password and Google Sign-In are enabled for this account.
                    </Typography>
                  ) : null}
                </Box>

                {hasPasswordAuth ? (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdatingPassword}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '9px',
                      px: 2.2,
                      py: 0.8,
                      fontSize: '0.84rem',
                      fontWeight: 700,
                      bgcolor: '#59B8DD',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#46ACD4',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                  </Button>
                ) : null}
              </Box>
            </Box>

            {isGoogleOnlyUser ? (
              <Box sx={{ maxWidth: 620 }}>
                <Typography sx={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.6 }}>
                  To change your password, go to your Google Account settings:
                </Typography>
                <Button
                  component="a"
                  href="https://myaccount.google.com/security"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  sx={{
                    mt: 1.6,
                    textTransform: 'none',
                    borderRadius: '9px',
                    px: 2.2,
                    py: 0.8,
                    fontSize: '0.84rem',
                    fontWeight: 700,
                    bgcolor: '#59B8DD',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#46ACD4',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Manage Google Account
                </Button>
                <Typography sx={{ mt: 1.2, color: '#7B8B97', fontSize: '0.84rem', lineHeight: 1.5 }}>
                  Manage Google Account {'->'} Security {'->'} Password
                </Typography>
              </Box>
            ) : hasPasswordAuth ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  columnGap: 1.5,
                  rowGap: 1.85,
                }}
              >
                <AppOutlinedTextField
                  fullWidth
                  sx={[roomyFieldSx, { gridColumn: { xs: 'auto', md: '1 / -1' } }]}
                  label="Current Password"
                  type={passwordVisibility.currentPassword ? 'text' : 'password'}
                  required
                  disabled={isUpdatingPassword}
                  value={passwordForm.currentPassword}
                  onChange={(event) => updatePasswordField('currentPassword', event.target.value)}
                  placeholder="Enter Current Password"
                  autoComplete="current-password"
                  InputProps={{
                    sx: { alignItems: 'center' },
                    endAdornment: renderPasswordVisibilityAdornment('currentPassword', 'current password'),
                  }}
                />
                <AppOutlinedTextField
                  fullWidth
                  sx={roomyFieldSx}
                  label="New Password"
                  type={passwordVisibility.newPassword ? 'text' : 'password'}
                  required
                  disabled={isUpdatingPassword}
                  value={passwordForm.newPassword}
                  onChange={(event) => updatePasswordField('newPassword', event.target.value)}
                  placeholder="Enter New Password"
                  autoComplete="new-password"
                  InputProps={{
                    sx: { alignItems: 'center' },
                    endAdornment: renderPasswordVisibilityAdornment('newPassword', 'new password'),
                  }}
                />
                <AppOutlinedTextField
                  fullWidth
                  sx={roomyFieldSx}
                  label="Confirm Password"
                  type={passwordVisibility.confirmPassword ? 'text' : 'password'}
                  required
                  disabled={isUpdatingPassword}
                  value={passwordForm.confirmPassword}
                  onChange={(event) => updatePasswordField('confirmPassword', event.target.value)}
                  placeholder="Confirm New Password"
                  autoComplete="new-password"
                  InputProps={{
                    sx: { alignItems: 'center' },
                    endAdornment: renderPasswordVisibilityAdornment('confirmPassword', 'password confirmation'),
                  }}
                />
              </Box>
            ) : (
              <Typography sx={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.6 }}>
                No supported sign-in method is configured. Please contact support.
              </Typography>
            )}

            {hasPasswordAuth && hasGoogleAuth ? (
              <Box sx={{ mt: 2.2, pt: 2, borderTop: '1px solid #e4ebf1' }}>
                <Typography sx={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.6 }}>
                  Google Sign-In is also linked to this account.
                </Typography>
                <Button
                  component="a"
                  href="https://myaccount.google.com/security"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{ mt: 1.2, textTransform: 'none', borderRadius: '9px', fontWeight: 700 }}
                >
                  Manage Google Account
                </Button>
              </Box>
            ) : null}
          </Box>

          <Box sx={[settingsCardSx, { mt: 2.1 }]}>
            <Typography sx={{ color: '#1B232A', fontWeight: 700, fontSize: '1.02rem' }}>Two-Factor Authentication</Typography>
            <Typography sx={{ mt: 0.55, color: '#7B8B97', fontSize: '0.84rem' }}>
              Add an extra layer of security to your account. During sign-in, you&apos;ll receive a One-Time Password (OTP) via email, which must be entered to successfully access your account.
            </Typography>

            <Box sx={{ mt: 2.3, display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Switch
                checked
                disabled
                inputProps={{ 'aria-label': 'Enable two-factor authentication' }}
                sx={securitySwitchSx}
              />
              <Typography sx={{ color: '#5A6A75', fontSize: '1rem', fontWeight: 500 }}>
                Enabled
              </Typography>
            </Box>
          </Box>

        </Box>
      </Paper>
    </Box>
  );
};

export default SecuritySettingsView;
